import {DOCUMENT, Location, PlatformLocation} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {slideInUpOnEnterAnimation} from '@app/animation/slideDownToUp';
import {CacheObjectService, DeviceService, EventTrackerService, LogService} from '@app/core';
import {checkConfirm, validationPattern} from '@app/helper/validation.formcontrol';
import {IndicatorService} from '@app/shared';
import {iNotification, NotificationService} from '@app/shared/notification/notification.service';
import * as moment from 'jalali-moment';
import {forkJoin, Subscription, SubscriptionLike} from 'rxjs';

import {UserService} from '../user-service.service';
import {PlayerService} from "@app/components/player/player.service";
import {NavigationBarService} from "@app/shared/navigation-bar/navigation-bar.service";

enum DialogType { gender, email, birthdate, occupation, password }

@Component({
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.scss'],
  animations: [slideInUpOnEnterAnimation({anchor: "slidePopup", translate: '100%', duration: 350})]
})

export class ProfileEditComponent implements OnInit, OnDestroy {

  public userForm: FormGroup;
  public emailForm: FormGroup;
  public passForm: FormGroup;

  public isSaving: boolean = false;
  public dialogType: DialogType;
  public dialogItems: typeof DialogType = DialogType;
  public isShowDialog: boolean = false;
  public maxYear: number;
  public days: Array<{ id: number, title: string }> = [];
  public selectDay: { id: number, title: string } | null = null;

  public months: Array<{ id: number, title: string }> = [];
  public selectMonth: { id: number, title: string } | null = null;

  public years: Array<{ id: number, title: string }> = [];
  public selectYear: { id: number, title: string } | null = null;

  public occupations: Array<{ id: string, title: string }>;
  public selectOccupation: { id: string, title: string } | null = null;
  public birthJal: string | null;

  private Location$: SubscriptionLike = new Subscription();

  public isMobileSignedUp: boolean = false;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly notification: NotificationService,
    private readonly cache: CacheObjectService<any>,
    private readonly log: LogService,
    private readonly formBuilder: FormBuilder,
    public readonly device: DeviceService,
    public readonly playService: PlayerService,
    public readonly navigationService: NavigationBarService,
    private readonly inidcator: IndicatorService,
    private readonly _location: PlatformLocation,
    private readonly location: Location,
    private readonly analytics: EventTrackerService,
    private readonly userService: UserService,
    private readonly render: Renderer2) {
    this.maxYear = moment().jYear();
    moment.locale('fa', {useGregorianParser: true});
    Array.from(this.range(1, 31)).forEach((x: number, i) => this.days.push({id: x, title: x.toString()}));
    moment().localeData().jMonths().forEach((x, i) => this.months.push({id: (i + 1), title: x}));
    Array.from(this.range(1300, this.maxYear)).forEach((x: number, i) => this.years.push({id: x, title: x.toString()}));
  }

  public onShowNotification(e: iNotification) {
    this.notification.setNotification(e);
  }

  public ngOnInit() {
    this.inidcator.hide();
    this.emailForm = this.formBuilder.group({
      email: [null, [Validators.pattern(validationPattern.email), Validators.required]],
      emailConfirm: [null, [Validators.pattern(validationPattern.email), Validators.required, (e) => checkConfirm(e, 'email')]],
      password: [null],
    }, {updateOn: 'change'});

    this.passForm = this.formBuilder.group({
      newPassword: [null, [Validators.required]],
      confirm: [null, [Validators.required, (e) => checkConfirm(e, 'newPassword')]],
      oldPassword: [null, [Validators.required]],
    }, {updateOn: 'change'})

    this.userForm = this.formBuilder.group({
      firstName: [null],
      lastName: [null],
      email: [null],
      birthday: [null],
      mobileNo: new FormControl({value: '', disabled: true}),
      gender: [null],
      occupation: [null],
      city: [null],
      address: [null],
      country: [null],
      postalCode: [null]
    });
    this.Location$ = this.location.subscribe(data => {
      this.closeDialog();
    });

    forkJoin(
      [this.cache.getObjectCache("/api/occupations"),
        this.userService.getUserData()]).subscribe(c => {
      const userData = c[1];
      this.isMobileSignedUp = c[1].mobileSignedUp;
      if (this.isMobileSignedUp) {
        this.emailForm.get('password').clearValidators();
        this.emailForm.get('password').setValue(0);
      } else {
        this.emailForm.get('password').setValidators([Validators.required]);
        this.emailForm.get('password').setValue(null);
      }
      this.occupations = c[0].map(z => ({id: z.occupationId, title: z.title}));
      this.selectOccupation = this.occupations.find(c => c.id === userData.occupation);
      this.userForm.patchValue(userData);
      this.prepareBirthdayValue();
    });
  }

  private prepareBirthdayValue() {
    const birthday = this.userForm.get('birthday')?.value;
    if (birthday) {
      try {
        const _m = moment(birthday);
        this.birthJal = _m.locale('fa').format('jYYYY/jM/jD');
        this.selectYear = this.years.find(z => z.id == _m.jYear());
        this.selectMonth = this.months.find(z => z.id == _m.jMonth() + 1);
        this.selectDay = this.days.find(z => z.id == _m.jDate());
      } catch (e) {
        this.birthJal = moment().format('jYYYY/jMM/jDD');
      }
    } else {
      this.birthJal = null;
    }
  }

  public openDialog(type: DialogType) {
    this.render.setStyle(this.document.body, 'overflow', 'hidden');
    this._location.pushState(null, null, this.location.path() + '#modalOpened');
    this.isShowDialog = true;
    this.dialogType = type;
  }

  public closeDialog() {
    switch (this.dialogType) {
      case DialogType.occupation:
        this.selectOccupation = this.occupations.find(c => c.id === this.userForm.get('occupation')?.value);
        break;
      case DialogType.email:
        this.emailForm.reset();
        break;
      case DialogType.password:
        this.passForm.reset();
        break;
      case DialogType.birthdate:
      case DialogType.gender:
      default:
        break;
    }
    this.render.removeStyle(this.document.body, 'overflow');
    if (this.location.path(true).includes('#modalOpened'))
      this.location.back();
    this.isShowDialog = false;
    this.dialogType = null;
  }

  public setUserData(): void {
    this.inidcator.showHude();
    this.isSaving = true;
    this.userService.setUserData(this.userForm.getRawValue())
      .subscribe(suc => {
        this.log.logger(suc);
        this.userService.setUserInfo(null);
        this.onShowNotification({text: 'اطلاعات شما بروز رسانی گردید.', type: 'info'});
        this.analytics.logEvent('x_action_result', {x_action_name: 'save-edit-profile', x_action_result: 'success'});
        this.isSaving = false;
        this.inidcator.hideHude();
      }, err => {
        this.log.logger(err);
        let error = 'Error';
        if (err.error.message) {
          error = err.error.message;
        }
        this.onShowNotification({text: error, type: 'error'});
        this.isSaving = false;
        this.inidcator.hideHude();
      }, () => {
        this.isSaving = false;
        this.inidcator.hideHude();
      });
  }

  private* range(start, end) {
    yield start;
    if (start === end) return;
    yield* this.range(start + 1, end);
  }

  public saveGender(value) {
    this.userForm.get('gender').setValue(value);
    this.closeDialog();
  }

  public saveBirthdate() {
    let date = `${this.selectYear?.title}/${this.selectMonth?.id}/${this.selectDay?.title}`;
    if (!moment(date, 'jYYYY/jMM/jDD').isValid()) {
      this.onShowNotification({text: 'تاریخ وارد شده اشتباه است', type: 'error', timeout: 5000});
      return;
    }
    this.birthJal = date;
    this.userForm.get('birthday').setValue(moment(date, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD'));
    this.closeDialog();
  }

  public saveOccupation() {
    this.userForm.get('occupation').setValue(this.selectOccupation.id);
    this.closeDialog();
  }

  public updateEmail() {
    if (this.emailForm.valid) {
      this.isSaving = true;
      this.userService.setUserEmail(this.emailForm.getRawValue())
        .subscribe(suc => {
          this.onShowNotification({text: 'ایمیل شما با موفقیت تغییر کرد', type: 'info'});
          this.isSaving = false;
          this.userForm.get('email').setValue(this.emailForm.get('email').value);
          this.emailForm.reset();
          this.closeDialog();
          this.userService.setUserInfo(null);
        }, err => {
          this.isSaving = false;
          if (err.status == 400) {
            this.onShowNotification({text: err.error.errors[0], type: 'error'});
          }
        });
    }
  }

  public swipe(action: string) {
  }

  public updatePass(): void {
    if (this.passForm.valid) {
      this.isSaving = true;
      this.userService.setUserPassword(this.passForm.getRawValue()).subscribe(
        (suc) => {
          this.onShowNotification({text: "رمز عبور تغییر یافت", type: "info"});
          this.isSaving = false;
          this.closeDialog();
        },
        (err) => {
          this.isSaving = false;
          if (err.status == 200) {
            this.onShowNotification({text: "رمز عبور تغییر یافت", type: "info"});
            this.closeDialog();
          }
          if (err.status == 400) {
            this.onShowNotification({text: err.error.errors[0], type: "error"});
          }
        }
      );
    }
  }

  ngOnDestroy() {
    if (this.Location$)
      this.Location$.unsubscribe();
  }

  calculateHeight(): number {
    let height: number = 0;
    switch (this.dialogType) {
      case DialogType.email:
      case DialogType.password:
        height = 430;
        if (this.navigationService.isShow) {
          height += 55;
        }
        if (this.playService.getStatusShowPlayer()) {
          height += 65;
        }
        break;
      default:
        height = 210;
        if (this.navigationService.isShow) {
          height += 55;
        }
        if (this.playService.getStatusShowPlayer()) {
          height += 65;
        }
        break;
    }
    return height;
  }
}
